import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import { createCrmInstitute } from '../../../services/crmInstituteService';
import { fetchUsers } from '../../../services/userService'; // API to fetch users

interface Institute {
  institute_name: string;
  status: string;
  assigned_to: number | '';
}

const AddInstitute: React.FC = () => {
  const navigate = useNavigate();
  const [institutes, setInstitutes] = useState<Institute[]>([
    { institute_name: '', status: 'Lead', assigned_to: '' }
  ]);

  const [users, setUsers] = useState<{ user_id: number; username: string }[]>(
    []
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const userData = await fetchUsers(); // Fetch available users
        setUsers(userData);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    loadUsers();
  }, []);

  // Handle input changes for dynamic form
  const handleChange = (index: number, field: string, value: any) => {
    const updatedInstitutes = [...institutes];
    updatedInstitutes[index] = { ...updatedInstitutes[index], [field]: value };
    setInstitutes(updatedInstitutes);
  };

  // Add a new empty institute row
  const addInstituteRow = () => {
    setInstitutes([
      ...institutes,
      { institute_name: '', status: 'Lead', assigned_to: '' }
    ]);
  };

  // Remove a specific institute row
  const removeInstituteRow = (index: number) => {
    if (institutes.length > 1) {
      setInstitutes(institutes.filter((_, i) => i !== index));
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    // Validate that all fields are filled
    if (institutes.some(inst => !inst.institute_name || !inst.status)) {
      setError('Please fill in all institute details.');
      setSubmitting(false);
      return;
    }

    try {
      await Promise.all(institutes.map(inst => createCrmInstitute(inst))); // Bulk add
      alert(`${institutes.length} Institute(s) added successfully`);
      navigate('/crm/institutes/all-institutes');
    } catch (error) {
      setError('Error adding institutes. Please try again.');
      console.error('Error submitting institutes:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container">
      <h2>Add Institute(s)</h2>
      {error && <Alert variant="danger">{error}</Alert>}

      <Form onSubmit={handleSubmit}>
        {institutes.map((inst, index) => (
          <div key={index} className="mb-3 p-3 border rounded">
            <h5>Institute {index + 1}</h5>

            {/* Institute Name */}
            <Form.Group>
              <Form.Label>Institute Name</Form.Label>
              <Form.Control
                type="text"
                value={inst.institute_name}
                onChange={e =>
                  handleChange(index, 'institute_name', e.target.value)
                }
                required
                disabled={submitting}
              />
            </Form.Group>

            {/* Status */}
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={inst.status}
                onChange={e => handleChange(index, 'status', e.target.value)}
                disabled={submitting}
              >
                <option value="Lead">Lead</option>
                <option value="Opportunity">Opportunity</option>
                <option value="Lost">Lost</option>
                <option value="Converted">Converted</option>
              </Form.Select>
            </Form.Group>

            {/* Assigned User */}
            <Form.Group>
              <Form.Label>Assigned To</Form.Label>
              <Form.Select
                value={inst.assigned_to}
                onChange={e =>
                  handleChange(index, 'assigned_to', Number(e.target.value))
                }
                disabled={submitting}
              >
                <option value="">Select User</option>
                {users.map(user => (
                  <option key={user.user_id} value={user.user_id}>
                    {user.username}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Remove Button */}
            {institutes.length > 1 && (
              <Button
                variant="danger"
                className="mt-2"
                onClick={() => removeInstituteRow(index)}
                disabled={submitting}
              >
                Remove
              </Button>
            )}
          </div>
        ))}

        {/* Add More Institutes Button */}
        <Button
          variant="primary"
          className="mt-3"
          onClick={addInstituteRow}
          disabled={submitting}
        >
          + Add Another Institute
        </Button>

        {/* Submit Button */}
        <Button type="submit" className="mt-3 ms-2" disabled={submitting}>
          {submitting ? <Spinner animation="border" size="sm" /> : 'Submit All'}
        </Button>
      </Form>
    </div>
  );
};

export default AddInstitute;
